import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormDropdown from 'components/KNForm/KNFormDropdown'
import KNFormPhone from 'components/KNForm/KNFormPhone'
import KNFormText from 'components/KNForm/KNFormText'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { getSharingLanguages } from 'global/helpers/languages'
import { regexEmail } from 'global/helpers/validators'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { createDriver, DRIVERS_QUERY_KEY, editDriver } from './DriverManager.service'
import type { CreateOrEditDriverFormValues, DriverData } from './DriverManager.types'

interface CreateOrEditDriverDialogPayload {
  driver: DriverData
}

interface CreateOrEditDriverDialogProps {
  payload?: CreateOrEditDriverDialogPayload
  open: boolean
  onAction: () => void
  onClose: () => void
}

const CreateOrEditDriverDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: CreateOrEditDriverDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, getValues, setError } = useForm<CreateOrEditDriverFormValues>()
  const queryClient = useQueryClient()
  const createDriverMutation = useMutation({
    mutationFn: createDriver,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DRIVERS_QUERY_KEY] }),
  })
  const updateDriverMutation = useMutation({
    mutationFn: editDriver,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [DRIVERS_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues({
          name: payload?.driver.name,
          contactNumber: payload?.driver.contactNumber,
          email: payload?.driver.email,
          internalId: payload?.driver.cid,
          language: payload?.driver.language,
        }),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<CreateOrEditDriverFormValues> = async (data: CreateOrEditDriverFormValues) => {
    await handleFormSubmission(
      () => payload ? updateDriverMutation.mutateAsync(data) : createDriverMutation.mutateAsync(data),
      {
        onError: (error) => setError('root', processServerErrorMessages(error)),
        onSuccess: () => {
          analyticsEvent(payload ? 'polestar_cs_driver_updated' : 'polestar_cs_driver_created')
          onAction()
        },
      },
    )
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={
        payload
          ? `${payload.driver.name} — ${t('screens.cs.driver_manager.card.actions.edit')}`
          : t('screens.cs.driver_manager.card.actions.new')
      }
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
        >
          {payload
            ? t('screens.cs.driver_manager.add_edit_driver.update')
            : t('screens.cs.driver_manager.add_edit_driver.create')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormText
            name="name"
            label={t('screens.cs.driver_manager.add_edit_driver.name')}
            control={control}
            rules={{
              required: t('form.validation.required'),
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormDropdown
            sx={{
              maxWidth: 'sm',
            }}
            name="language"
            label={t('screens.cs.driver_manager.add_edit_driver.language')}
            control={control}
            options={getSharingLanguages().map((language) => {
              return {
                label: language.label,
                value: language.languageCode,
              }
            })}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormText
            name="email"
            label={t('screens.cs.driver_manager.add_edit_driver.email')}
            control={control}
            rules={{
              pattern: {
                value: regexEmail,
                message: t('form.validation.invalid_email'),
              },
              validate: {
                phoneOrEmail: (value) =>
                  value || getValues('contactNumber')
                    ? true
                    : t('screens.cs.driver_manager.add_edit_driver.phone_or_email_required'),
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <KNFormPhone
            name="contactNumber"
            label={t('screens.cs.driver_manager.add_edit_driver.phone')}
            control={control}
            rules={{
              validate: {
                phoneOrEmail: (value) =>
                  value || getValues('email')
                    ? true
                    : t('screens.cs.driver_manager.add_edit_driver.phone_or_email_required'),
              },
            }}
            getValues={getValues}
          />
        </Grid>
      </Grid>
    </KNDialog>
  )
}

export default CreateOrEditDriverDialog
